import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useModal } from '@/contexts/modal'
import AddIcons from '@/icons/AddIcons'
import { CustomerContract } from '@/modules/Contract/types'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { IPermissionProps } from '@/types/shared'
import { resources, scopes } from '@/utils/permission'
// Two different Buttons on purpose. DropdownMenuTrigger asChild hands the trigger a ref, and
// vnetwork-ui's Button is a plain React.FC that drops it — the menu then never opens. The shadcn
// Button is forwardRef, which is why every working dropdown in this repo uses it as the trigger
// (see data-table-customized/toolbar/column-visibility-dropdown.tsx). The plain no-permission
// branch keeps the vnetwork-ui Button so it stays identical to the button it replaced.
import { Button as TriggerButton } from '@/components/ui/button'
import { ChevronDown } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'

interface ISubcontractAddMenuProps extends IPermissionProps {
    contract: CustomerContract
    contractID: number
}

/**
 * Single entry point for the two ways an annex (phụ lục) comes into existence.
 *
 * They are deliberately separate menu items, not one merged form: creating an annex adds something,
 * while converting DESTROYS a contract (it is soft-deleted, its number is locked forever, and there
 * is no undo in v1). Collapsing both into one flow would make an irreversible action reachable by
 * accident, so the menu makes the user choose which one they mean before any form opens.
 */
const SubcontractAddMenu: React.FC<ISubcontractAddMenuProps> = ({
    contract,
    contractID = 0,
}) => {
    const { t } = useTranslation()
    const { open, setModal } = useModal()
    const { isAdmin, permissions } = usePermissionsStore()

    // Same gate the API route uses, so the menu never offers an action the server will 403.
    const canConvert = useMemo(() => {
        if (isAdmin) return true
        const contractScopes =
            permissions.find((p) => p?.rsname === resources.contractsCustomers)
                ?.scopes || []
        return contractScopes.includes(scopes.create)
    }, [isAdmin, permissions])

    // Without the convert option there is nothing to choose, so fall back to a plain button rather
    // than a one-item dropdown.
    if (!canConvert) {
        return (
            <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcons />}
                onClick={() =>
                    setModal('ContractModals/CreateSubcontractModal/index', {
                        contract,
                        contractID,
                    })
                }
                disabled={open}
            >
                {t('subcontract-create')}
            </Button>
        )
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={open}>
                <TriggerButton type="button" variant="outline" size="sm">
                    <AddIcons />
                    {t('contract:subcontract-add')}
                    <ChevronDown className="w-4 h-4" />
                </TriggerButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-64">
                <DropdownMenuItem
                    onSelect={() =>
                        setModal(
                            'ContractModals/CreateSubcontractModal/index',
                            {
                                contract,
                                contractID,
                            }
                        )
                    }
                >
                    {t('subcontract-create')}
                </DropdownMenuItem>
                <DropdownMenuItem
                    onSelect={() =>
                        setModal(
                            'ContractModals/ConvertContractToSubcontractModal/index',
                            { contract, contractID }
                        )
                    }
                >
                    <div className="flex flex-col">
                        <span>
                            {t('contract:convert-contract-to-subcontract')}
                        </span>
                        <span className="text-xs text-muted-foreground">
                            {t('contract:convert-contract-to-subcontract-hint')}
                        </span>
                    </div>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default SubcontractAddMenu
